/**
 * Style variables for use in javascript.
 */
module.exports = {

    /**
     * The formFactor which is currently applicable.
     *
     * @type {string}
     * @public
     */
    formFactor: "tiny",

    /**
     * Cached width of the window.
     *
     * @type {number}
     * @public
     */
    windowWidth: null,

    /**
     * Cached height of the window.
     *
     * @type {number}
     * @public
     */
    windowHeight: null,

    /**
     * Cached aspect ratio of the window.
     *
     * @type {number}
     * @public
     */
    windowAspectRatio: null,

    /**
     * Cached multiplier to convert `1rem` to pixels.
     *
     * @type {number}
     * @public
     */
    remToPx: null,

    /**
     * Cached multiplier for page spacing. Used to reduce or increase all
     * spacings on certain breakpoints.
     *
     * @type {number}
     * @public
     */
    pageSpacingMultiplier: null,

    /**
     * Cached multiplier for grid spacing. Used to reduce or increase all
     * spacings on certain breakpoints.
     *
     * @type {number}
     * @public
     */
    gridSpacingMultiplier: null,

    /**
     * Color used for placeholders, in hsla format.
     *
     * @type  {Object}
     * @public
     */
    placeholderColor: {
        h: 0,
        s: 0,
        l: 20,
        a: 1
    },

    /**
     * Logo image type. Currently supported: "svg", "png"
     */
    logoKind: require("app/ui/logo/SvgLogo"),

    /**
     * Base colors for background graphic
     */
    backgroundBaseHues: [338, 159],

    /**
     * Radiant MP Styles
     */
    RadiantMP: {
        skinBackgroundColor: "171717",
        skinButtonColor:     "f9fafa",
        skinAccentColor:     "2edc9f",
        iconsSet:            "material",
        buttonHeight:        48,
        playerHeight:        48
    },

    /**
     * Safe area spy
     */
    safeAreaSpy: (function() {
        var spy;

        spy                     = document.createElement("div");
        spy.style.position      = "absolute";
        spy.style.visibility    = "hidden";
        spy.style.pointerEvents = "none";
        spy.style.top           = "0px";
        spy.style.left          = "0px";
        spy.style.width         = "0px";
        spy.style.height        = "0px";
        spy.style.paddingTop    = "env(safe-area-inset-top, 0px)";
        spy.style.paddingLeft   = "env(safe-area-inset-left, 0px)";
        spy.style.paddingBottom = "env(safe-area-inset-bottom, 0px)";
        spy.style.paddingRight  = "env(safe-area-inset-right, 0px)";

        return spy;
    })(),

    /**
     * Update the variables based on window dimensions.
     *
     * @public
     */
    reflow: function() {
        var safeAreaSpy  = this.safeAreaSpy,
            safeAreaStyle,
            windowWidth  = window.innerWidth,
            windowHeight = window.innerHeight;

        // Window dimensions
        this.windowWidth       = windowWidth;
        this.windowHeight      = windowHeight;
        this.windowAspectRatio = windowWidth / windowHeight;

        // Safe area
        document.documentElement.append(safeAreaSpy);
        safeAreaStyle            = window.getComputedStyle(safeAreaSpy);
        this.safeAreaInsetTop    = parseFloat(safeAreaStyle.paddingTop, 10);
        this.safeAreaInsetLeft   = parseFloat(safeAreaStyle.paddingLeft, 10);
        this.safeAreaInsetBottom = parseFloat(safeAreaStyle.paddingBottom, 10);
        this.safeAreaInsetRight  = parseFloat(safeAreaStyle.paddingRight, 10);
        safeAreaSpy.remove();

        // FormFactor value
        if (windowWidth < 375) {
            this.formFactor = "tiny";
        }
        else if (windowWidth >= 375 && windowWidth < 480) {
            this.formFactor = "smaller";
        }
        else if (windowWidth >= 480 && windowWidth < 640) {
            this.formFactor = "small";
        }
        else if (windowWidth >= 640 && windowWidth < 960) {
            this.formFactor = "medium";
        }
        else if (windowWidth >= 960) {
            this.formFactor = "large";
        }

        // Base font size breakpoints
        if (windowWidth < 375) {
            this.remToPx = 14;
        }
        else if (windowWidth >= 375 && windowWidth < 480) {
            this.remToPx = 15;
        }
        else if (windowWidth >= 480) {
            this.remToPx = 16;
        }

        // Header font sizes
        if (windowWidth < 480) {
            this.headerFontSize = {
                h1: (28 / 16),
                h2: (23 / 16),
                h3: (20 / 16),
                h4: (18 / 16),
                h5: 1,
                h6: (14 / 16)
            };
        }
        else if (windowWidth >= 480 && windowWidth < 640) {
            this.headerFontSize = {
                h1: (30 / 16),
                h2: (24 / 16),
                h3: (22 / 16),
                h4: (19 / 16),
                h5: 1,
                h6: (14 / 16)
            };
        }
        else if (windowWidth > 640) {
            this.headerFontSize = {
                h1: (34 / 16),
                h2: (26 / 16),
                h3: (24 / 16),
                h4: (20 / 16),
                h5: 1,
                h6: (14 / 16)
            };
        }

        // Spacing multipliers
        if (windowWidth < 375) {
            this.pageSpacingMultiplier = 0.75;
            this.gridSpacingMultiplier = 1;
        }
        else if (windowWidth >= 375 && windowWidth < 480) {
            this.pageSpacingMultiplier = 0.875;
            this.gridSpacingMultiplier = 1.5;
        }
        else if (windowWidth >= 480) {
            this.pageSpacingMultiplier = 1;
            this.gridSpacingMultiplier = 2;
        }
    },

    // App Header
    // --------------------------------

    /**
     * Whether the app header area floats on top of app content area.
     *
     * @type {number}
     * @public
     */
    appHeaderFloat: true,

    /**
     * The gap in pixels between buttons in the header.
     *
     * @type  {number}
     * @public
     */
    appHeaderSpacerGap: 12,

    /**
     * The gap in pixels between buttons in the header.
     *
     * @type  {number}
     * @public
     */
    appHeaderButtonGap: 24,

    /**
     * Whether the app header is allowed to disable captions on some buttons in
     * order to show as many buttons as possible within the available screen
     * realestate.
     *
     * @type  {boolean}
     * @public
     */
    appHeaderHasIconMode: true,

    /**
     * Whether the app header should move all optional buttons into the
     * MenuDropdown if any of them don't fit in the header bar.
     *
     * @type  {boolean}
     * @public
     */
    appHeaderMoveAllButtonsToMenuDropdown: true,

    // Font Size Multipliers
    // --------------------------------

    /**
     * Multiplier for small text.
     *
     * @type {number}
     * @public
     */
    fontSizeSmall: (14 / 16),

    /**
     * Multiplier for large text.
     *
     * @type {number}
     * @public
     */
    fontSizeLarge: (18 / 16),

    // Header Text Sizes
    // --------------------------------

    /**
     * @public
     */
    headerFontSize: {

        /**
         * The size of h1 header in rem.
         *
         * @type {number}
         * @public
         */
        h1: null,

        /**
         * The size of h1 header in rem.
         *
         * @type {number}
         * @public
         */
        h2: null,

        /**
         * The size of h1 header in rem.
         *
         * @type {number}
         * @public
         */
        h3: null,

        /**
         * The size of h1 header in rem.
         *
         * @type {number}
         * @public
         */
        h4: null,

        /**
         * The size of h1 header in rem.
         *
         * @type {number}
         * @public
         */
        h5: null,

        /**
         * The size of h1 header in rem.
         *
         * @type {number}
         * @public
         */
        h6: null
    },

    /**
     * Get the font-size of a header in pixels.
     *
     * @param  {string}  size  The header size (h1, h2, h3, h4, h5 or h6)
     * @return {number} The font-size in pixels
     * @public
     */
    getHeaderFontSizePx: function(size) {
        return this.headerFontSize[size] * this.remToPx;
    },

    /**
     * Header line-height multiplier.
     *
     * @type {number}
     * @public
     */
    headerLineHeight: 1.3,

    /**
     * Get the line-height of a header in pixels.
     *
     * @param  {string}  size  The header size (h1, h2, h3, h4, h5 or h6)
     * @return {number} The line-height in pixels
     * @public
     */
    getHeaderLineHeightPx: function(size) {
        return this.getHeaderFontSizePx(size) * this.headerLineHeight;
    },

    /**
     * Header margin-top in rem.
     *
     * @type {number}
     * @public
     */
    headerMarginTop: 0,

    /**
     * Get the header margin-top in pixels.
     *
     * @return {number} The margin-top in pixels
     * @public
     */
    getHeaderMarginTopPx: function() {
        return this.headerMarginTop * this.remToPx;
    },

    /**
     * Header margin-bottom in rem.
     *
     * @type {number}
     * @public
     */
    headerMarginBottom: 1,

    /**
     * Get the header margin-bottom in pixels.
     *
     * @return {number} The margin-bottom in pixels
     * @public
     */
    getHeaderMarginBottomPx: function() {
        return this.headerMarginBottom * this.remToPx;
    },

    // Body Text Sizes
    // --------------------------------

    /**
     * The size of body text in rem.
     *
     * @type {number}
     * @public
     */
    bodyFontSize: 1,

    /**
     * Get the font-size of body text in pixels.
     *
     * @return {number} The font-size in pixels
     * @public
     */
    getBodyFontSizePx: function() {
        return this.bodyFontSize * this.remToPx;
    },

    /**
     * Body text line-height multiplier
     *
     * @type {number}
     * @public
     */
    bodyLineHeight: 1.5,

    /**
     * Get the line-height of a body text in pixels.
     *
     * @return {number} The line-height in pixels
     * @public
     */
    getBodyLineHeightPx: function() {
        return this.getBodyFontSizePx() * this.bodyLineHeight;
    },

    /**
     * Paragraph margin-top in rem.
     *
     * @type {number}
     * @public
     */
    paragraphMarginTop: 0,

    /**
     * Get the paragraph margin-top in pixels.
     *
     * @return {number} The margin-top in pixels
     * @public
     */
    getParagraphMarginTopPx: function() {
        return this.paragraphMarginTop * this.remToPx;
    },

    /**
     * Paragraph margin-bottom in rem.
     *
     * @type {number}
     * @public
     */
    paragraphMarginBottom: 1,

    /**
     * Get the paragraph margin-bottom in pixels.
     *
     * @return {number} The margin-bottom in pixels
     * @public
     */
    getParagraphMarginBottomPx: function() {
        return this.paragraphMarginBottom * this.remToPx;
    },

    // Page Spacings
    // --------------------------------

    /**
     * Spacing at the edges of the page in rem.
     *
     * @type {number}
     * @public
     */
    pageMargin: 1.5,

    /**
     * Get the spacing at the edges of the page in pixels.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getPageMarginPx: function() {
        return this.pageMargin * this.remToPx * this.pageSpacingMultiplier;
    },

    /**
     * Get the spacing at the edges of the page in pixels.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getPageMarginLargePx: function() {
        var buttonPaddingHorizontal = this.getButtonPaddingHorizontalCompactPx(),
            bodyLineHeightPx        = this.getBodyLineHeightPx();

        if (this.windowWidth >= 960) {
            return buttonPaddingHorizontal + bodyLineHeightPx + buttonPaddingHorizontal;
        }
        else {
            return this.getPageMarginPx();
        }
    },

    // Grid Spacings
    // --------------------------------

    /**
     * Horizontal spacing around grids in rem.
     *
     * @type {number}
     * @public
     */
    gridOuterSpacingHorizontal: 0.5,

    /**
     * Get the horizontal spacing around grids in pixels.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getGridOuterSpacingHorizontalPx: function() {
        return this.gridOuterSpacingHorizontal * this.remToPx * this.gridSpacingMultiplier;
    },

    /**
     * Vertical spacing around grids in rem.
     *
     * @type {number}
     * @public
     */
    gridOuterSpacingVertical: 0.5,

    /**
     * Get the vertical spacing around grids in pixels.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getGridOuterSpacingVerticalPx: function() {
        return this.gridOuterSpacingVertical * this.remToPx * this.gridSpacingMultiplier;
    },

    /**
     * Horizontal spacing between grid items in rem.
     *
     * @type {number}
     * @public
     */
    gridInnerSpacingHorizontal: 0.5,

    /**
     * Get the horizontal spacing between grid items in pixels.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getGridInnerSpacingHorizontalPx: function() {
        return this.gridInnerSpacingHorizontal * this.remToPx * this.gridSpacingMultiplier;
    },

    /**
     * Vertical spacing between grid items in rem.
     *
     * @type {number}
     * @public
     */
    gridInnerSpacingVertical: 0.5,

    /**
     * Get the vertical spacing between grid items in pixels.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getGridInnerSpacingVerticalPx: function() {
        return this.gridInnerSpacingVertical * this.remToPx * this.gridSpacingMultiplier;
    },

    // Content Spacings
    // --------------------------------

    /**
     * Horizontal padding of content blocks in rem.
     *
     * @type {number}
     * @public
     */
    contentPaddingHorizontal: 1,

    /**
     * Get the horizontal padding of content blocks in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getContentPaddingHorizontalPx: function() {
        return this.contentPaddingHorizontal * this.remToPx;
    },

    /**
     * Vertical padding of content blocks in rem.
     *
     * @type {number}
     * @public
     */
    contentPaddingVertical: 1,

    /**
     * Get the vertical padding of content blocks in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getContentPaddingVerticalPx: function() {
        return this.contentPaddingVertical * this.remToPx;
    },

    /**
     * Horizontal padding of compact content areas in rem.
     *
     * @type {number}
     * @public
     */
    contentPaddingHorizontalCompact: 0.5,

    /**
     * Get the horizontal padding of compact content areas in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getContentPaddingHorizontalCompactPx: function() {
        return this.contentPaddingHorizontalCompact * this.remToPx;
    },

    /**
     * Vertical padding of compact content areas in rem.
     *
     * @type {number}
     * @public
     */
    contentPaddingVerticalCompact: 0.5,

    /**
     * Get the vertical padding of compact content areas in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getContentPaddingVerticalCompactPx: function() {
        return this.contentPaddingVerticalCompact * this.remToPx;
    },

    // Buttons
    // --------------------------------

    /**
     * Horizontal padding of buttons in rem.
     *
     * @type {number}
     * @public
     */
    buttonPaddingHorizontal: 1,

    /**
     * Get the horizontal padding of buttons in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getButtonPaddingHorizontalPx: function() {
        return this.buttonPaddingHorizontal * this.remToPx;
    },

    /**
     * Vertical padding of buttons in rem.
     *
     * @type {number}
     * @public
     */
    buttonPaddingVertical: 0.75,

    /**
     * Get the vertical padding of buttons in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getButtonPaddingVerticalPx: function() {
        return this.buttonPaddingVertical * this.remToPx;
    },

    /**
     * Horizontal padding of compact buttons in rem.
     *
     * @type {number}
     * @public
     */
    buttonPaddingHorizontalCompact: 0.75,

    /**
     * Get the horizontal padding of compact buttons in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getButtonPaddingHorizontalCompactPx: function() {
        return this.buttonPaddingHorizontalCompact * this.remToPx;
    },

    /**
     * Vertical padding of compact buttons in rem.
     *
     * @type {number}
     * @public
     */
    buttonPaddingVerticalCompact: 0.5,

    /**
     * Get the vertical padding of compact buttons in pixels.
     *
     * @return {number} The padding in pixels
     * @public
     */
    getButtonPaddingVerticalCompactPx: function() {
        return this.buttonPaddingVerticalCompact * this.remToPx;
    },

    /**
     * Horizontal spacing between buttons in rem.
     *
     * @type {number}
     * @public
     */
    buttonSpacingHorizontal: 1,

    /**
     * Get horizontal spacing between buttons in px.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getButtonSpacingHorizontalPx: function() {
        return this.buttonSpacingHorizontal * this.remToPx;
    },

    /**
     * Vertical spacing between buttons in rem.
     *
     * @type {number}
     * @public
     */
    buttonSpacingVertical: 0.75,

    /**
     * Get vertical spacing between buttons in px.
     *
     * @return {number} The spacing in pixels
     * @public
     */
    getButtonSpacingVerticalPx: function() {
        return this.buttonSpacingVertical * this.remToPx;
    },

    // Ident
    // --------------------------------

    /**
     * Optional ident to show in the media player
     */
    // ident: null,
    ident: require("app/theme/assets/ident.png"),

    /**
     * Ident width in pixels
     */
    identWidth: 64,

    /**
     * Ident height in pixels
     */
    identHeight: 64,

    /**
     * Min ident width in pixels
     */
    identMinWidth: 24,

    /**
     * Min ident height in pixels
     */
    identMinHeight: 24,

    /**
     * Max ident width in pixels
     */
    identMaxWidth: 64,

    /**
     * Max ident height in pixels
     */
    identMaxHeight: 64

};
